import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  FileSignature, FileText, Download, Upload, Send, PenLine, CheckCircle2,
  Clock, Lock, AlertTriangle, X, Plus, User as UserIcon, Filter, RefreshCw, Trash2, Sparkles, Inbox,
  Undo2, ExternalLink, LayoutGrid, List as ListIcon
} from "lucide-react";

export function NosignApprovalsBanner({ onChanged }) {
  const [reqs, setReqs] = useState([]);
  const [audit, setAudit] = useState(null);
  const load = () => api.get("/hr/documents/nosign-requests").then((r) => setReqs(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const decide = async (docId, approve) => {
    try {
      await api.post(`/hr/documents/${docId}/nosign-decision`, { approve });
      toast.success(approve ? "Approved — employee can download the no-sign copy" : "Request denied");
      load(); onChanged && onChanged();
      if (audit !== null) loadAudit();
    } catch (e) { toast.error(e.response?.data?.detail || "Decision failed"); }
  };

  const loadAudit = () => api.get("/hr/documents/nosign-audit").then((r) => setAudit(r.data || [])).catch(() => setAudit([]));

  return (
    <div className={`brut-card border-purple-700 ${reqs.length ? "bg-purple-50" : "bg-white"} p-3 mb-4`} data-testid="nosign-approvals-banner">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="label-mono text-purple-800">NO-SIGN DOWNLOAD REQUESTS · {reqs.length} PENDING</div>
        <button onClick={() => (audit === null ? loadAudit() : setAudit(null))} className="brut-btn-secondary px-2 py-0.5 text-[9px] font-mono border-purple-700 text-purple-800" data-testid="nosign-audit-toggle">
          {audit === null ? "▼ AUDIT LOG" : "▲ HIDE AUDIT"}
        </button>
      </div>
      {reqs.length > 0 && (
      <div className="space-y-1.5 mt-2">
        {reqs.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-2 border border-purple-300 bg-white px-2 py-1.5" data-testid={`nosign-req-${r.id}`}>
            <span className="font-mono text-xs font-bold">{r.field_values?.employee_name || r.employee_id}</span>
            <span className="font-mono text-[10px] text-zinc-500">{r.template_type} · requested {(r.nosign_request?.requested_at || "").slice(0, 10)}</span>
            <div className="ml-auto flex gap-1">
              <button onClick={() => decide(r.id, true)} className="brut-btn px-2.5 py-1 text-[10px] bg-green-700 text-white hover:bg-green-800" data-testid={`approve-nosign-${r.id}`}>APPROVE</button>
              <button onClick={() => decide(r.id, false)} className="brut-btn-secondary px-2.5 py-1 text-[10px] border-red-700 text-red-700 hover:bg-red-50" data-testid={`deny-nosign-${r.id}`}>DENY</button>
            </div>
          </div>
        ))}
      </div>
      )}
      {audit !== null && (
        <div className="mt-2 border-t border-purple-200 pt-2 max-h-48 overflow-y-auto" data-testid="nosign-audit-panel">
          {audit.length === 0 && <div className="font-mono text-[10px] text-zinc-400">NO AUDIT ENTRIES YET</div>}
          {audit.map((a) => (
            <div key={a.id} className="font-mono text-[10px] flex flex-wrap gap-2 border-b border-zinc-100 py-0.5">
              <span>{(a.at || "").slice(0, 16).replace("T", " ")}</span>
              <b className={a.event === "approved" ? "text-green-700" : a.event === "denied" ? "text-red-700" : "text-purple-700"}>{(a.event || "").toUpperCase()}</b>
              <span>{a.template_type}</span>
              <span className="text-zinc-500">by {a.by}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

